import { MenuCategory } from "../components/MenuCategory";
import { MenuItemCard } from "../components/MenuItemCard";
import type { NormalizedMenu } from "../lib/menuTypes";
import { useMenu } from "./useMenu";
import type { CSSProperties, RefObject } from "react";

export function IndexPage() {
  const { menu, status, error, refresh } = useMenu(false);

  return (
    <main className="page-shell">
      <header className="site-header">
        <div>
          <p className="eyebrow">Chozen Yogurt</p>
          <h1>Bus Menu</h1>
        </div>
        <button className="refresh-button" onClick={refresh} type="button">
          Refresh
        </button>
      </header>
      <MenuContent menu={menu} status={status} error={error} />
    </main>
  );
}

export function MenuContent({
  menu,
  status,
  error,
  displayMode = false,
  contentRef,
  displayColumns,
  displayDensity = "comfortable",
  displayScale = 1
}: {
  menu?: NormalizedMenu;
  status: "loading" | "ready" | "error";
  error?: string;
  displayMode?: boolean;
  contentRef?: RefObject<HTMLDivElement | null>;
  displayColumns?: number;
  displayDensity?: "comfortable" | "compact" | "dense";
  displayScale?: number;
}) {
  if (status === "loading") {
    return <div className="state-panel">Loading today&apos;s menu...</div>;
  }

  if (status === "error" && !menu) {
    return (
      <div className="state-panel state-panel--error">
        <h2>Menu unavailable</h2>
        <p>{error || "Please check back in a moment."}</p>
      </div>
    );
  }

  if (!menu || menu.categories.every((category) => category.items.length === 0)) {
    return <div className="state-panel">No menu items are available right now.</div>;
  }

  const contentStyle = displayMode
    ? ({
        "--display-columns": displayColumns,
        "--display-scale": displayScale
      } as CSSProperties)
    : undefined;
  const displayToppingsItem = displayMode
    ? menu.categories.flatMap((category) => category.items).find((item) => isToppingsItem(item.name) || isToppingsItem(item.customerFacingName))
    : undefined;
  const categories = displayToppingsItem
    ? menu.categories
        .map((category) => ({
          ...category,
          items: category.items.filter((item) => item.id !== displayToppingsItem.id)
        }))
        .filter((category) => category.items.length > 0)
    : menu.categories;

  return (
    <div
      className={
        displayMode
          ? `menu-content menu-content--display menu-content--display-${displayDensity}`
          : "menu-content"
      }
      ref={contentRef}
      style={contentStyle}
    >
      {menu.source === "fallback" ? <div className="notice">Showing saved menu while live Square data is unavailable.</div> : null}
      {categories.map((category) => (
        <MenuCategory category={category} displayMode={displayMode} key={category.id} />
      ))}
      {displayToppingsItem ? (
        <section className="display-toppings-section">
          <MenuItemCard item={displayToppingsItem} displayMode />
        </section>
      ) : null}
    </div>
  );
}

function isToppingsItem(name?: string) {
  return name?.trim().toLowerCase() === "toppings";
}
